import React, { useState, useEffect } from 'react';
import axios from '../utils/axios';
import { useNavigate } from 'react-router-dom';
import BranchLeadManagement from './BranchLeadManagement';
import BranchDropdownOptionsManager from './BranchDropdownOptionsManager';
import LeadForm from './LeadForm';
import {
  BuildingOfficeIcon,
  UserGroupIcon,
  ChartBarIcon,
  DocumentTextIcon,
  ArrowRightOnRectangleIcon,
  HomeIcon,
  PlusCircleIcon,
  Cog6ToothIcon
} from '@heroicons/react/24/outline';

const BranchDashboardNew = () => {
  const navigate = useNavigate();
  const [branchInfo, setBranchInfo] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [showAddLeadModal, setShowAddLeadModal] = useState(false);
  const [recentLeads, setRecentLeads] = useState([]);

  useEffect(() => {
    const token = localStorage.getItem('branchToken');
    const storedBranchInfo = localStorage.getItem('branchInfo');

    if (!token || !storedBranchInfo) {
      navigate('/login');
      return;
    }

    setBranchInfo(JSON.parse(storedBranchInfo));
    fetchBranchData(token);
  }, [navigate]);

  const fetchBranchData = async (token) => {
    try {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

      // Use branch-auth endpoint for branch-specific stats
      const statsResponse = await axios.get('/branch-auth/stats');
      setStats(statsResponse.data.data);

      // Fetch recent leads
      const leadsResponse = await axios.get('/branch-auth/leads');
      setRecentLeads(leadsResponse.data.data?.slice(0, 5) || []);

      setLoading(false);
    } catch (error) {
      console.error('Error fetching branch data:', error);
      if (error.response?.status === 401) {
        handleLogout();
      }
      setLoading(false);
    }
  };

  const handleAddLead = async (leadData) => {
    try {
      const token = localStorage.getItem('branchToken');
      
      // Add branch ID and company ID to lead data
      const companyId = branchInfo.company?._id || branchInfo.company;
      const branchLeadData = {
        ...leadData,
        assignedBranch: branchInfo._id,
        company: companyId
      };

      console.log('🔍 Branch Lead Data:', branchLeadData);

      await axios.post('/leads', branchLeadData, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      setShowAddLeadModal(false);
      alert('✅ Lead added successfully!');
      
      // Refresh data
      fetchBranchData(token);
    } catch (error) {
      console.error('Error adding lead:', error);
      alert('❌ Failed to add lead: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('branchToken');
    localStorage.removeItem('branchInfo');
    navigate('/login');
  };

  const menuItems = [
    { id: 'dashboard', icon: <HomeIcon className="w-6 h-6" />, label: 'Dashboard' },
    { id: 'leads', icon: <DocumentTextIcon className="w-6 h-6" />, label: 'Lead Management' },
    { id: 'dropdown-options', icon: <Cog6ToothIcon className="w-6 h-6" />, label: 'Dropdown Options' },
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading branch dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <aside className={`${sidebarCollapsed ? 'w-20' : 'w-64'} bg-gradient-to-b from-purple-900 to-purple-800 text-white transition-all duration-300 ease-in-out flex flex-col shadow-xl fixed h-screen z-20`}>
        {/* Logo & Brand */}
        <div className="p-6 border-b border-purple-700">
          <div className="flex items-center justify-between">
            {!sidebarCollapsed && (
              <div>
                <h2 className="text-xl font-bold">{branchInfo?.name}</h2>
                <p className="text-purple-200 text-xs mt-1">Branch Dashboard</p>
              </div>
            )}
            <button
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              className="p-2 rounded-lg hover:bg-purple-700 transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {sidebarCollapsed ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
                )}
              </svg>
            </button>
          </div>
        </div>

        {/* Navigation Menu */}
        <nav className="flex-1 px-3 py-6 space-y-2 overflow-y-auto">
          {menuItems.map((item) => (
            <button
              key={item.id}
              onClick={() => setActiveTab(item.id)}
              className={`w-full flex items-center ${sidebarCollapsed ? 'justify-center' : 'justify-start'} px-4 py-3 rounded-lg transition-all duration-200 ${
                activeTab === item.id
                  ? 'bg-purple-600 shadow-lg transform scale-105'
                  : 'hover:bg-purple-700 hover:translate-x-1'
              }`}
              title={sidebarCollapsed ? item.label : ''}
            >
              {item.icon}
              {!sidebarCollapsed && (
                <span className="ml-3 font-medium text-sm">{item.label}</span>
              )}
            </button>
          ))}
        </nav>

        {/* User Info & Logout */}
        <div className="p-4 border-t border-purple-700">
          {!sidebarCollapsed && (
            <div className="mb-3">
              <p className="text-sm font-medium truncate">{branchInfo?.username}</p>
              <p className="text-xs text-purple-200 truncate">{branchInfo?.branchCode}</p>
            </div>
          )}
          <button
            onClick={handleLogout}
            className="w-full flex items-center justify-center px-4 py-2 bg-purple-700 hover:bg-purple-600 rounded-lg transition-colors"
            title="Logout"
          >
            <ArrowRightOnRectangleIcon className="w-5 h-5" />
            {!sidebarCollapsed && <span className="ml-2 text-sm">Logout</span>}
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className={`flex-1 ${sidebarCollapsed ? 'ml-20' : 'ml-64'} transition-all duration-300`}>
        {/* Header */}
        <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-10">
          <div className="px-6 py-4">
            <div className="flex justify-between items-center">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">
                  {activeTab === 'dashboard' ? 'Dashboard Overview' : 
                   activeTab === 'leads' ? 'Lead Management' : 
                   'Form Builder'}
                </h1>
                <p className="text-sm text-gray-600 mt-1">
                  {branchInfo?.city}, {branchInfo?.state}
                </p>
              </div>
            </div>
          </div>
        </header>

        {/* Content Area */}
        <div className="p-6">
          {activeTab === 'dashboard' && (
            <>
              {/* Quick Actions */}
              <div className="mb-6 flex justify-end">
                <button
                  onClick={() => setShowAddLeadModal(true)}
                  className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors shadow-md"
                >
                  <PlusCircleIcon className="h-5 w-5" />
                  Add New Lead
                </button>
              </div>

              {/* Stats Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                {/* Employees Card */}
                <div className="bg-white rounded-lg shadow p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-500 text-sm font-medium">Total Employees</p>
                      <p className="text-3xl font-bold text-gray-900 mt-2">
                        {stats?.employees?.total || 0}
                      </p>
                      <p className="text-sm text-green-600 mt-1">
                        {stats?.employees?.active || 0} Active
                      </p>
                    </div>
                    <UserGroupIcon className="h-12 w-12 text-purple-600" />
                  </div>
                </div>

                {/* Leads Card */}
                <div className="bg-white rounded-lg shadow p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-500 text-sm font-medium">Total Leads</p>
                      <p className="text-3xl font-bold text-gray-900 mt-2">
                        {stats?.leads?.total || 0}
                      </p>
                      <p className="text-sm text-blue-600 mt-1">
                        Branch Leads
                      </p>
                    </div>
                    <DocumentTextIcon className="h-12 w-12 text-blue-600" />
                  </div>
                </div>

                {/* Performance Card */}
                <div className="bg-white rounded-lg shadow p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-gray-500 text-sm font-medium">Branch Status</p>
                      <p className="text-xl font-bold text-green-600 mt-2">
                        {branchInfo?.isActive ? 'Active' : 'Inactive'}
                      </p>
                      <p className="text-sm text-gray-600 mt-1">
                        {branchInfo?.branchCode}
                      </p>
                    </div>
                    <ChartBarIcon className="h-12 w-12 text-green-600" />
                  </div>
                </div>
              </div>

              {/* Branch Information */}
              <div className="bg-white rounded-lg shadow mb-8">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h2 className="text-xl font-bold text-gray-900">Branch Information</h2>
                </div>
                <div className="p-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <p className="text-sm font-medium text-gray-500">Branch Name</p>
                      <p className="mt-1 text-lg text-gray-900">{branchInfo?.name}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500">Branch Code</p>
                      <p className="mt-1 text-lg text-gray-900">{branchInfo?.branchCode}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500">Username</p>
                      <p className="mt-1 text-lg text-gray-900">{branchInfo?.username}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500">Email</p>
                      <p className="mt-1 text-lg text-gray-900">{branchInfo?.email || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500">Phone</p>
                      <p className="mt-1 text-lg text-gray-900">{branchInfo?.phone || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500">Address</p>
                      <p className="mt-1 text-lg text-gray-900">
                        {branchInfo?.address}, {branchInfo?.city}, {branchInfo?.state} - {branchInfo?.pincode}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Recent Leads */}
              <div className="bg-white rounded-lg shadow mb-8">
                <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                  <h2 className="text-xl font-bold text-gray-900">Recent Leads</h2>
                  <button
                    onClick={() => setActiveTab('leads')}
                    className="text-purple-600 hover:text-purple-800 text-sm font-medium"
                  >
                    View All →
                  </button>
                </div>
                <div className="p-6">
                  {recentLeads.length === 0 ? (
                    <div className="text-center py-8">
                      <DocumentTextIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-500 mb-4">No leads yet</p>
                      <button
                        onClick={() => setShowAddLeadModal(true)}
                        className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg inline-flex items-center gap-2"
                      >
                        <PlusCircleIcon className="h-5 w-5" />
                        Add First Lead
                      </button>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contact</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Product</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {recentLeads.map((lead) => (
                            <tr key={lead._id} className="hover:bg-gray-50">
                              <td className="px-4 py-3 text-sm text-gray-900">{lead.customerName}</td>
                              <td className="px-4 py-3 text-sm text-gray-500">{lead.contactNumber}</td>
                              <td className="px-4 py-3 text-sm text-gray-500">{lead.productOfInterest || 'N/A'}</td>
                              <td className="px-4 py-3">
                                <span className={`px-2 py-1 text-xs rounded-full ${
                                  lead.status === 'new' ? 'bg-blue-100 text-blue-800' :
                                  lead.status === 'contacted' ? 'bg-yellow-100 text-yellow-800' :
                                  lead.status === 'qualified' ? 'bg-green-100 text-green-800' :
                                  'bg-gray-100 text-gray-800'
                                }`}>
                                  {lead.status}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>

              {/* Permissions */}
              <div className="bg-white rounded-lg shadow">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h2 className="text-xl font-bold text-gray-900">Branch Permissions</h2>
                </div>
                <div className="p-6">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {Object.entries(branchInfo?.permissions || {}).map(([key, value]) => (
                      <div key={key} className="flex items-center space-x-2">
                        <div className={`w-3 h-3 rounded-full ${value ? 'bg-green-500' : 'bg-red-500'}`}></div>
                        <span className="text-sm text-gray-700">
                          {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </>
          )}

          {activeTab === 'leads' && (
            <BranchLeadManagement />
          )}

          {activeTab === 'dropdown-options' && (
            <BranchDropdownOptionsManager />
          )}
        </div>
      </main>

      {/* Add Lead Modal */}
      {showAddLeadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <LeadForm
              lead={null}
              onSubmit={handleAddLead}
              onCancel={() => setShowAddLeadModal(false)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default BranchDashboardNew;
